/**
 * MissionSection.tsx - Mission Statement Display Component
 *
 * This component displays the fundraiser's mission statement with title and
 * description, providing context and motivation for potential donors.
 *
 * Key Features:
 * - Mission title and description display
 * - Responsive typography and spacing
 * - Integration with builder data for live updates
 * - Fallback content for empty states
 * - Consistent styling with other content sections
 *
 * Content Sources:
 * - Uses missionTitle and missionDescription from builder data
 * - Falls back to selected fundraiser mission data
 * - Provides default content if none specified
 *
 * Props: None (uses MobX store for data)
 *
 * Dependencies: MobX store, styled-components, Lucide icons
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../stores/FundraiserStore";
import { lightenColor } from "../utils/colorUtils";
import { FundraiserCustomColorConfig } from "../models";

const MissionContainer = styled.div`
  border-radius: 16px;
  padding: 16px 48px 0px;
  margin: 16px 0 10px 0;
  text-align: center;
`;

const Title = styled.h2<{ textColor: string }>`
  font-size: 36px;
  font-weight: 800;
  color: ${(props) => props.textColor};
  margin-bottom: 16px;
  line-height: 1.2;
`;

const Description = styled.p<{ textColor: string }>`
  font-size: 18px;
  color: ${(props) => props.textColor};
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto 32px;
  white-space: pre-line;
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ImpactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 20px 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.9);
  }
`;

const ImpactIcon = styled.div<{ iconColor: string; bgColor: string }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.iconColor};
  margin-bottom: 8px;
  box-shadow: 0 4px 16px ${(props) => props.iconColor}20;
`;

const ImpactNumber = styled.div<{ textColor: string }>`
  font-size: 28px;
  font-weight: 800;
  color: ${(props) => props.textColor};
  margin-bottom: 4px;
`;

const ImpactLabel = styled.div<{ textColor: string }>`
  font-size: 14px;
  color: ${(props) => props.textColor};
  font-weight: 600;
  text-align: center;
`;

interface HeaderProps {
  customColors: FundraiserCustomColorConfig;
}

export const MissionSection: React.FC<HeaderProps> = observer(
  ({ customColors }) => {
    // Use live builder data when available, otherwise fall back to saved fundraiser data
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const hasLiveBuilderData = fundraiserStore.selectedFundraiserId === null;

    // Use the selected text color for title, and a slightly lighter version for description
    const titleColor = customColors.text;
    const descriptionColor = lightenColor(customColors.text.hex, 25); // 25% lighter

    const missionTitle = hasLiveBuilderData
      ? builderData.missionTitle || ""
      : builderData.missionTitle || "";

    const missionDescription = hasLiveBuilderData
      ? builderData.missionDescription
      : selectedFundraiser?.missionDescription;

    const formattedDescription = missionDescription.includes("\n")
      ? missionDescription
      : missionDescription.replace(/\. /g, ".\n");

    return (
      <MissionContainer>
        <Title textColor={titleColor.rgba || titleColor.hex}>
          {missionTitle}
        </Title>
        <Description textColor={descriptionColor}>
          {formattedDescription}
        </Description>
      </MissionContainer>
    );
  }
);
